// SubscriptionPage — Subscription Management
// Layout:
//   - Top nav: < Back | "Subscription"
//   - Hero: current plan + renewal date
//   - Plan features
//   - Usage stats
//   - Billing history
//   - Cancel / change plan CTA

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TierPill } from '../../components/Brand';

interface Invoice {
  id: string;
  date: string;
  amount: number;
  description: string;
  status: 'paid' | 'pending' | 'failed';
  invoice: string;
}

const INVOICES: Invoice[] = [
  { id: 'i1', date: '2026-07-04', amount: 79,   description: 'Beginner plan — monthly',      status: 'paid', invoice: 'INV-202607-4821' },
  { id: 'i2', date: '2026-06-04', amount: 79,   description: 'Beginner plan — monthly',      status: 'paid', invoice: 'INV-202606-3214' },
  { id: 'i3', date: '2026-05-04', amount: 79,   description: 'Beginner plan — monthly',      status: 'paid', invoice: 'INV-202605-1873' },
  { id: 'i4', date: '2026-04-04', amount: 79,   description: 'Beginner plan — monthly',      status: 'failed', invoice: 'INV-202604-0921' },
];

export function SubscriptionPage() {
  const nav = useNavigate();
  const [autoRenew, setAutoRenew] = useState(true);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Subscription</div>
        <div className="w-9" />
      </div>

      {/* Hero — current plan */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero text-center" style={{ background: 'linear-gradient(135deg, #34C759 0%, #1E7E34 100%)', color: 'white' }}>
          <div className="text-[11px] uppercase tracking-wider text-white/80">Current plan</div>
          <div className="mt-1 text-[36px] font-bold tracking-tight">Beginner</div>
          <TierPill tier="Beginner" />
          <div className="mt-3 grid grid-cols-2 gap-2 text-center">
            <div className="rounded-[12px] bg-white/15 backdrop-blur-md p-2.5">
              <div className="text-[10px] text-white/70 uppercase">Renews</div>
              <div className="text-[14px] font-bold tabular-nums">Aug 4, 2026</div>
            </div>
            <div className="rounded-[12px] bg-white/15 backdrop-blur-md p-2.5">
              <div className="text-[10px] text-white/70 uppercase">Price</div>
              <div className="text-[14px] font-bold tabular-nums">$79.00 / mo</div>
            </div>
          </div>
        </div>
      </div>

      {/* Auto-renew */}
      <div className="mx-4 mt-3 ios26-list bg-white">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue/10 text-[16px]">🔄</div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-medium text-tf-text-1">Auto-renew</div>
            <div className="text-[12px] text-tf-text-3">Renews automatically on Aug 4</div>
          </div>
          <button
            onClick={() => setAutoRenew(!autoRenew)}
            data-testid="auto-renew"
            className={['relative h-7 w-12 rounded-full transition-colors', autoRenew ? 'bg-[#34C759]' : 'bg-[#E5E5EA]'].join(' ')}
          >
            <div className={['absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all', autoRenew ? 'left-[22px]' : 'left-0.5'].join(' ')} />
          </button>
        </div>
      </div>

      {/* Usage this month */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">This month</h2>
      <div className="mx-4 grid grid-cols-3 gap-2">
        <UsageTile label="Trades"   value="87"  limit="Unlimited" color="#34C759" />
        <UsageTile label="Bots"     value="1"   limit="1 included" color="#34C759" />
        <UsageTile label="Data"     value="12d" limit="Real-time"  color="#34C759" />
      </div>

      {/* Plan features */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">What's included</h2>
      <div className="mx-4 ios26-list bg-white">
        <FeatureRow included title="$50K account size" sub="First challenge activation" />
        <FeatureRow included title="1 active bot"     sub="AI-powered strategies" />
        <FeatureRow included title="Up to 4 strategies" sub="Breakout, Mean Reversion" />
        <FeatureRow included title="Daily loss alert"  sub="30% threshold" />
        <FeatureRow included title="EOD drawdown"      sub="5% total" />
        <FeatureRow included title="80% profit split"  sub="First $5K, then 90%" />
        <FeatureRow title="Custom AI bot strategies" sub="Pro plan only" />
        <FeatureRow title="Live newsfeed+ alerts"   sub="Pro plan only" />
        <FeatureRow title="API access"              sub="Pro plan only" last />
      </div>

      {/* Upgrade CTA */}
      <div className="mx-4 mt-4">
        <button onClick={() => nav('/portal/tiers')} data-testid="change-plan" className="flex h-12 w-full items-center justify-center btn-spring">
          Upgrade plan
        </button>
      </div>

      {/* Billing history */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Billing history</h2>
      <div className="mx-4 ios26-list bg-white">
        {INVOICES.map((inv, i) => (
          <div
            key={inv.id}
            data-testid={`invoice-${inv.id}`}
            className={['flex items-center gap-3 px-4 py-3', i < INVOICES.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-surface-1 text-[16px]">📄</div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-tf-text-1">{inv.description}</div>
              <div className="text-[11px] text-tf-text-3">{new Date(inv.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {inv.invoice}</div>
            </div>
            <div className="text-right">
              <div className="text-[14px] font-bold tabular-nums text-tf-text-1">${inv.amount}.00</div>
              <div className={['rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase inline-block',
                inv.status === 'paid' ? 'bg-[#34C759]/15 text-tf-profit' :
                inv.status === 'pending' ? 'bg-[#FF9500]/15 text-tf-warning' :
                'bg-[#FF3B30]/15 text-tf-loss'].join(' ')}>
                {inv.status}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Cancel */}
      <div className="mx-4 mt-4">
        <button
          onClick={() => { if (confirm('Cancel subscription? It will remain active until Aug 4, 2026.')) alert('Subscription cancelled.'); }}
          data-testid="cancel-subscription"
          className="flex h-12 w-full items-center justify-center rounded-full bg-white border border-[#FF3B30] text-[15px] font-semibold text-tf-loss active:opacity-40"
        >
          Cancel subscription
        </button>
      </div>
    </div>
  );
}

function UsageTile({ label, value, limit, color }: { label: string; value: string; limit: string; color: string }) {
  return (
    <div className="rounded-[12px] border border-tf-border bg-white p-3 text-center">
      <div className="text-[11px] uppercase tracking-wide text-tf-text-3">{label}</div>
      <div className="mt-1 text-[20px] font-bold tabular-nums" style={{ color }}>{value}</div>
      <div className="text-[10px] text-tf-text-3">{limit}</div>
    </div>
  );
}

function FeatureRow({ title, sub, included, last }: { title: string; sub: string; included?: boolean; last?: boolean }) {
  return (
    <div className={['flex items-center gap-3 px-4 py-2.5', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <div className={['flex h-6 w-6 items-center justify-center rounded-full', included ? 'bg-[#34C759]' : 'bg-tf-surface-1'].join(' ')}>
        {included ? (
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        ) : (
          <span className="text-[10px] text-tf-text-3">—</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className={['text-[14px] font-medium', included ? 'text-tf-text-1' : 'text-tf-text-3'].join(' ')}>{title}</div>
        <div className="text-[11px] text-tf-text-3">{sub}</div>
      </div>
    </div>
  );
}
